/**
 * Solve One-Step Inequalities generator (6.PAFR.1.4).
 *
 * Builds a one-step inequality around a known solution with the equation-simple
 * engine, then reports the solved inequality as the answer. All operands are
 * positive, so multiply/divide never flip the inequality — appropriate for 6th
 * grade. The solution is verified against the engine's own solutionOp, so the
 * prompt and graded answer always agree. A number-line ray of the solution set
 * is attached because the curriculum flags this topic as graphing.
 *
 * Families:
 *   F1: x + b (op) c   solve by subtracting
 *   F2: x - b (op) c   solve by adding
 *   F3: b * x (op) c   solve by dividing (positive b, no flip)
 *   F4: x / b (op) c   solve by multiplying (positive b, no flip)
 */

import { rat } from "../engine/rational";
import { Rng } from "../engine/rng";
import { getTopic } from "../curriculum/curriculum";
import {
  buildInequality,
  type OneStepInequality,
  type OneStepOperation,
} from "../engine/equation-simple";
import { rayNumberLine } from "../graph/number-line";
import type { InequalityOp } from "../engine/answer";
import type {
  Difficulty,
  GenerateContext,
  Generator,
  Question,
  QuestionSignature,
} from "./types";

const TOPIC = "solve-inequalities" as const;
const VERSION = "1.0.0";

type Family = "F1" | "F2" | "F3" | "F4";

const OP_FAMILY: Record<Family, OneStepOperation> = {
  F1: "add",
  F2: "subtract",
  F3: "multiply",
  F4: "divide",
};

const OP_LATEX: Record<InequalityOp, string> = {
  ">=": "\\geq",
  "<=": "\\leq",
  ">": ">",
  "<": "<",
};

interface Cfg {
  readonly families: readonly Family[];
  readonly operandMax: number;
  readonly solMin: number;
  readonly solMax: number;
}

const DIFF: Record<Difficulty, Cfg> = {
  easy: { families: ["F1", "F2"], operandMax: 9, solMin: 1, solMax: 12 },
  standard: { families: ["F1", "F2", "F3", "F4"], operandMax: 10, solMin: 1, solMax: 12 },
  hard: { families: ["F1", "F2", "F3", "F4"], operandMax: 12, solMin: 1, solMax: 15 },
  challenge: { families: ["F3", "F4"], operandMax: 12, solMin: 1, solMax: 15 },
};

const INEQ_OPS: readonly InequalityOp[] = ["<", "<=", ">", ">="];

/** Render the original (unsolved) inequality in LaTeX. */
function inequalityToLatex(ineq: OneStepInequality): string {
  const v = ineq.variable;
  const op = ineq.operand.toLatex();
  const res = ineq.result.toLatex();
  const sym = OP_LATEX[ineq.op];
  switch (ineq.operation) {
    case "add":
      return `${v} + ${op} ${sym} ${res}`;
    case "subtract":
      return `${v} - ${op} ${sym} ${res}`;
    case "multiply":
      return ineq.operand.isInteger() ? `${op}${v} ${sym} ${res}` : `${op} \\cdot ${v} ${sym} ${res}`;
    case "divide":
      return `\\frac{${v}}{${op}} ${sym} ${res}`;
  }
}

/** Render the original inequality as plain text. */
function inequalityToText(ineq: OneStepInequality): string {
  const v = ineq.variable;
  const op = ineq.operand.toString();
  const res = ineq.result.toString();
  switch (ineq.operation) {
    case "add":
      return `${v} + ${op} ${ineq.op} ${res}`;
    case "subtract":
      return `${v} - ${op} ${ineq.op} ${res}`;
    case "multiply":
      return `${op} * ${v} ${ineq.op} ${res}`;
    case "divide":
      return `${v} / ${op} ${ineq.op} ${res}`;
  }
}

function inverseDescription(operation: OneStepOperation, operand: string): string {
  switch (operation) {
    case "add":
      return `Subtract ${operand} from both sides.`;
    case "subtract":
      return `Add ${operand} to both sides.`;
    case "multiply":
      return `Divide both sides by ${operand}.`;
    case "divide":
      return `Multiply both sides by ${operand}.`;
  }
}

export class SolveInequalitiesGenerator implements Generator {
  readonly topic = TOPIC;
  readonly version = VERSION;
  readonly supportedDifficulties: readonly Difficulty[] = [
    "easy",
    "standard",
    "hard",
    "challenge",
  ];

  generate(ctx: GenerateContext): Question {
    const topic = getTopic(TOPIC);
    for (let attempt = 0; attempt < 100; attempt++) {
      const rng = new Rng(`${ctx.seed}#${attempt}`);
      const cfg = DIFF[ctx.difficulty];
      const family = rng.pick(cfg.families);
      const operation = OP_FAMILY[family];
      const v = "x";

      // Operands positive -> multiply/divide never flip the inequality.
      const operand = rat(rng.int(2, cfg.operandMax));
      const solution = rat(rng.intNonZero(cfg.solMin, cfg.solMax));
      const op = rng.pick(INEQ_OPS);

      const ineq = buildInequality(v, operation, operand, solution, op);

      // Division-form problems: require a tidy integer on the right side.
      if (operation === "divide" && !ineq.result.isInteger()) continue;
      // With positive operands the solution direction should not flip.
      if (ineq.solutionOp !== op) continue;

      const solvedLatex = `${v} ${OP_LATEX[ineq.solutionOp]} ${solution.toLatex()}`;
      const steps = [
        {
          description: inverseDescription(operation, operand.toString()),
          latex: solvedLatex,
        },
        {
          description: "The inequality symbol does not change because the operand is positive.",
          latex: solvedLatex,
        },
      ];

      return {
        id: `${TOPIC}-${ctx.seed}-${family}`,
        generator: TOPIC,
        generatorVersion: VERSION,
        seed: ctx.seed,
        unit: topic.unit,
        topic: TOPIC,
        family,
        difficulty: ctx.difficulty,
        prompt: {
          text: `Solve for ${v}: ${inequalityToText(ineq)}`,
          latex: inequalityToLatex(ineq),
        },
        answerType: "inequality",
        answer: { kind: "inequality", op: ineq.solutionOp, value: solution },
        steps,
        hints: [
          { level: 1, text: "Use the inverse operation to isolate the variable, just like an equation." },
          { level: 2, text: "Keep the inequality symbol the same when you add, subtract, or use a positive multiplier/divisor." },
        ],
        subskills: topic.subskills,
        graph: rayNumberLine(ineq.solutionOp, solution),
      };
    }
    throw new Error(`${TOPIC}: failed after 100 attempts (${ctx.seed}, ${ctx.difficulty})`);
  }

  signature(q: Question): QuestionSignature {
    const structure =
      q.answer.kind === "inequality"
        ? `${q.answer.op}${q.answer.value.key()}`
        : q.answer.kind;
    return { generator: TOPIC, family: q.family, difficulty: q.difficulty, structure };
  }
}

export const solveInequalities = new SolveInequalitiesGenerator();
